/**
 * App.js - Composant principal de l'application
 * 
 * Ce fichier est le point d'entrée de l'interface React.
 * Il configure le système de routage qui permet de naviguer entre les différentes pages.
 * 
 * Concepts importants:
 * - React Router: bibliothèque pour gérer la navigation dans une application React
 * - Lazy loading: technique pour charger les pages uniquement quand on en a besoin
 * - Routes protégées: pages accessibles seulement si l'utilisateur est connecté
 * - Error Boundary: composant qui capture les erreurs pour éviter que l'application ne plante
 * 
 * Structure du fichier:
 * 1. Import des bibliothèques et composants
 * 2. Composant de chargement (affiché pendant le lazy loading)
 * 3. Composant de layout protégé (vérifie l'authentification)
 * 4. Composant principal App (configure toutes les routes)
 */

import React, { Suspense } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "./features/auth/hooks/useAuth";
import Sidebar from "./shared/components/Sidebar";
import ToastHost from "./shared/components/ToastHost";
import ErrorBoundary from "./shared/components/ErrorBoundary";
import {
  Login,
  ProgrammeList,
  ProgrammeForm,
  ProgrammeParticipants,
  InfosList,
  InfoForm,
  PeopleList,
  SettingsDays,
  SettingsPartnersBanner,
  SettingsSocialLinks,
  SettingsLocations,
  SettingsInfoCategories,
} from "./navigation/routes";

// ============================================================================
// COMPOSANTS UI
// ============================================================================

/**
 * Composant de chargement affiché pendant le lazy loading
 * 
 * Le lazy loading est une technique qui permet de charger les pages uniquement
 * quand l'utilisateur y accède, ce qui rend l'application plus rapide au démarrage.
 * Pendant ce chargement, ce composant affiche un message de chargement.
 * 
 * Suspense est un composant React qui gère automatiquement l'affichage de ce
 * composant pendant qu'une page lazy-loaded est en train de charger.
 */
function ComposantChargement() {
  return (
    <div style={{
      // Flexbox pour centrer le contenu
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      // Prend toute la hauteur de l'écran
      minHeight: "100vh",
      // Couleur de fond (avec fallback si la variable CSS n'existe pas)
      background: "var(--bg-soft, #f5f5f5)",
    }}>
      <div style={{ 
        color: "var(--brand-xd, #352394)", 
        fontSize: 18 
      }}>
        Chargement...
      </div>
    </div>
  );
}

/**
 * Layout protégé pour les routes nécessitant une authentification
 * 
 * Ce composant est utilisé comme wrapper pour toutes les pages qui nécessitent
 * que l'utilisateur soit connecté. Il effectue les actions suivantes:
 * 1. Vérifie si l'utilisateur est authentifié via le store d'authentification
 * 2. Si non authentifié, redirige vers la page de login
 * 3. Si authentifié, affiche la sidebar et le contenu de la page
 * 
 * Le composant Outlet est un composant spécial de React Router qui affiche
 * le contenu de la route enfant (la page demandée par l'utilisateur).
 */
function LayoutProtege() {
  // Récupérer l'URL actuelle
  const localisation = useLocation();
  
  // Récupérer l'état d'authentification depuis le store Zustand
  const estAuthentifie = useAuthStore((state) => state.isAuthenticated);
  
  // Si l'utilisateur n'est pas authentifié, rediriger vers la page de login
  if (!estAuthentifie) {
    return (
      <Navigate 
        to="/login" 
        state={{ from: localisation }} 
        replace 
      />
    );
  }
  
  // Si l'utilisateur est authentifié, afficher le layout avec la sidebar
  return (
    <>
      <Sidebar />
      <main className="sb-main">
        <Outlet />
      </main>
      <ToastHost position="bottom-left" />
    </>
  );
}

// ============================================================================
// APPLICATION PRINCIPALE
// ============================================================================

/**
 * Composant principal de l'application
 * 
 * Ce composant configure toutes les routes de l'application.
 * Chaque route associe une URL à un composant React.
 * 
 * Les routes sont organisées en deux catégories:
 * - Routes publiques: accessibles sans connexion (ex: page de login)
 * - Routes protégées: accessibles seulement si connecté (ex: liste des programmes)
 */
export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<ComposantChargement />}>
        <Routes>
          {/* ==================================================================
              ROUTES PUBLIQUES (sans authentification)
              ================================================================== */}
          
          {/* Route pour la page de connexion */}
          <Route path="/login" element={<Login />} />

          {/* ==================================================================
              ROUTES PROTÉGÉES (nécessitent authentification)
              ================================================================== */}
          
          {/* Le composant LayoutProtege englobe toutes les routes protégées */}
          <Route element={<LayoutProtege />}>
            
            {/* Redirection de la racine vers la page programme */}
            <Route path="/" element={<Navigate to="/programme" replace />} />

            {/* ==================================================================
                SECTION: PROGRAMME
                ================================================================== */}
            
            {/* Liste des programmes */}
            <Route path="/programme" element={<ProgrammeList />} />
            
            {/* Création d'un nouveau programme */}
            <Route path="/programme/new" element={<ProgrammeForm />} />
            
            {/* Édition d'un programme existant (:id est un paramètre dynamique) */}
            <Route path="/programme/edit/:id" element={<ProgrammeForm />} />
            
            {/* Gestion des participants d'une session */}
            <Route
              path="/programme/:id/participants"
              element={<ProgrammeParticipants />}
            />

            {/* ==================================================================
                SECTION: INFOS PRATIQUES
                ================================================================== */}
            
            {/* Liste des infos pratiques */}
            <Route path="/infos" element={<InfosList />} />
            
            {/* Création d'une nouvelle info pratique */}
            <Route path="/infos/new" element={<InfoForm />} />
            
            {/* Édition d'une info pratique existante */}
            <Route path="/infos/edit/:id" element={<InfoForm />} />

            {/* ==================================================================
                SECTION: PERSONNES
                ================================================================== */}
            
            {/* Liste de toutes les personnes */}
            <Route path="/people" element={<PeopleList />} />

            {/* ==================================================================
                SECTION: CONFIGURATION
                ================================================================== */}
            
            {/* Configuration des jours de l'événement */}
            <Route path="/settings/days" element={<SettingsDays />} />
            
            {/* Configuration de la bannière partenaires */}
            <Route
              path="/settings/partners-banner"
              element={<SettingsPartnersBanner />}
            />
            
            {/* Configuration des liens réseaux sociaux */}
            <Route
              path="/settings/social-links"
              element={<SettingsSocialLinks />}
            />
            
            {/* Configuration des lieux */}
            <Route
              path="/settings/locations"
              element={<SettingsLocations />}
            />
            
            {/* Configuration des catégories d'infos */}
            <Route
              path="/settings/info-categories"
              element={<SettingsInfoCategories />}
            />
          </Route>

          {/* ==================================================================
              ROUTE PAR DÉFAUT (fallback)
              ================================================================== */}
          
          {/* Si l'URL ne correspond à aucune route, rediriger vers programme */}
          <Route path="*" element={<Navigate to="/programme" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
